import type { AxiosInstance } from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Globe, Search } from "lucide-react";

import { apiErrorMessage } from "../lib/api.js";
import { cn } from "../lib/cn.js";
import { rangeProblem, type DateRange } from "../lib/dates.js";
import { cellText, findReport, LANDING_PAGE_COLUMNS, type ReportColumn, type ReportRow } from "../lib/reports.js";
import { DataAsOfNote } from "../components/DataAsOfNote.js";
import { DateRangePicker } from "../components/DateRangePicker.js";
import { ExportButton } from "../components/ExportButton.js";
import { ReportGuideAccordion } from "../components/ReportGuide/ReportGuideAccordion.js";
import { ReportGuideToggleButton } from "../components/ReportGuide/ReportGuideToggleButton.js";
import { reportGuideContent } from "./reportGuides.js";
import { TableResizeHandle, useTableResize } from "../components/TableResizeHandle.js";
import { Input } from "../components/ui/input.js";
import { Skeleton } from "../components/ui/skeleton.js";

interface PeriodItem {
  readonly period_num: number;
  readonly period_label: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly display_label: string;
  readonly [key: string]: string | number;
}

export function LandingPageTrendScreen({
  client,
  tenantId,
  range,
  onRangeChange,
}: {
  readonly client: AxiosInstance;
  readonly tenantId: string;
  readonly range: DateRange;
  readonly onRangeChange: (next: DateRange) => void;
  readonly storeName?: string;
}): React.JSX.Element {
  const [guideOpen, setGuideOpen] = useState(false);

  const problem = rangeProblem(range);

  // Range duration in days (inclusive)
  const rangeDays = useMemo(() => {
    const fromTime = new Date(`${range.from}T00:00:00Z`).getTime();
    const toTime = new Date(`${range.to}T00:00:00Z`).getTime();
    return Math.round((toTime - fromTime) / 86400000) + 1;
  }, [range.from, range.to]);

  const weekDisabled = rangeDays <= 7;

  // Breakdown: "week" | "day"
  const [breakdown, setBreakdown] = useState<"week" | "day">("week");

  // If date range is 7 days or less, force breakdown to "day"
  useEffect(() => {
    if (weekDisabled && breakdown === "week") {
      setBreakdown("day");
    }
  }, [weekDisabled, breakdown]);

  // Ranking metric tab: "session_count" | "order_value" | "customer_count"
  const [rankingTab, setRankingTab] = useState<"session_count" | "order_value" | "customer_count">("session_count");

  const [searchQuery, setSearchQuery] = useState("");
  const URLS_CHUNK_SIZE = 4;
  const [loading, setLoading] = useState(false);
  const [loadingMoreUrls, setLoadingMoreUrls] = useState(false);
  const loadingMoreUrlsRef = useRef(false);
  loadingMoreUrlsRef.current = loadingMoreUrls;

  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<readonly ReportRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState<Record<string, number> | null>(null);
  // Only the setter is used — the value is echoed by the API and not rendered here.
  const [timezone, setTimezone] = useState<string | null>(null);
  const [dataAsOf, setDataAsOf] = useState<string | null>(null);

  // Batched breakdown periods: { [url]: PeriodItem[] }
  const [breakdownsByUrl, setBreakdownsByUrl] = useState<Record<string, readonly PeriodItem[]>>({});
  const [breakdownLoading, setBreakdownLoading] = useState<Record<string, boolean>>({});
  const [breakdownLoadingMore, setBreakdownLoadingMore] = useState<Record<string, boolean>>({});
  const [breakdownHasMore, setBreakdownHasMore] = useState<Record<string, boolean>>({});

  const breakdownsByUrlRef = useRef<Record<string, readonly PeriodItem[]>>({});
  breakdownsByUrlRef.current = breakdownsByUrl;

  const breakdownLoadingRef = useRef<Record<string, boolean>>({});
  breakdownLoadingRef.current = breakdownLoading;

  const breakdownLoadingMoreRef = useRef<Record<string, boolean>>({});
  breakdownLoadingMoreRef.current = breakdownLoadingMore;

  const breakdownHasMoreRef = useRef<Record<string, boolean>>({});
  breakdownHasMoreRef.current = breakdownHasMore;

  const rowElementsRef = useRef<Record<string, HTMLTableRowElement | null>>({});
  const generationRef = useRef(0);

  const ticket = useRef(0);

  // Metric columns from LANDING_PAGE_COLUMNS (all 17 columns after 'url')
  const metricColumns = useMemo(() => LANDING_PAGE_COLUMNS.slice(1), []);

  // Columns for the table header: first column is the breakdown label, followed by the 17 metrics
  const headerColumns = useMemo<readonly ReportColumn[]>(() => {
    const firstCol: ReportColumn = {
      key: "breakdown_label",
      label: breakdown === "week" ? "Landing Page & Week Breakdown" : "Landing Page & Day Breakdown",
      kind: "text",
      width: 320,
    };
    return [firstCol, ...metricColumns];
  }, [breakdown, metricColumns]);

  const initialWidths = useMemo(() => headerColumns.map((c) => c.width ?? 140), [headerColumns]);
  const { colWidths, startResize } = useTableResize(initialWidths, 80);
  const totalTableWidth = useMemo(() => colWidths.reduce((a, b) => a + b, 0), [colWidths]);

  const metricColumnsWidths = useMemo(() => colWidths.slice(1), [colWidths]);
  const metricColumnsTotalWidth = useMemo(
    () => metricColumnsWidths.reduce((a, b) => a + b, 0),
    [metricColumnsWidths]
  );

  const outerTableRef = useRef<HTMLDivElement | null>(null);
  const leftScrollRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const rightScrollRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const BREAKDOWN_CHUNK_SIZE = 15;

  // Active breakdown request pool to limit concurrent ClickHouse queries to max 2
  const activeBreakdownRequestsRef = useRef(0);
  const breakdownQueueRef = useRef<Array<() => Promise<void>>>([]);

  const processBreakdownQueue = useCallback(() => {
    while (activeBreakdownRequestsRef.current < 2 && breakdownQueueRef.current.length > 0) {
      const nextTask = breakdownQueueRef.current.shift();
      if (nextTask) {
        activeBreakdownRequestsRef.current++;
        // `void`: the queue drains itself in the callback, so there is no promise to await
        // here and an unobserved rejection would otherwise be invisible (.ai/02).
        void nextTask().finally(() => {
          activeBreakdownRequestsRef.current--;
          processBreakdownQueue();
        });
      }
    }
  }, []);

  // Dimension 2: Fetch breakdown for multiple landing page URLs in a single batch request
  const fetchBatchBreakdowns = useCallback(async (urls: readonly string[]) => {
    const missingUrls = urls.filter(
      (u) => u && !breakdownsByUrlRef.current[u] && !breakdownLoadingRef.current[u]
    );
    if (missingUrls.length === 0) return;

    const currentGen = generationRef.current;
    setBreakdownLoading((prev) => {
      const next = { ...prev };
      for (const u of missingUrls) next[u] = true;
      return next;
    });
    for (const u of missingUrls) {
      breakdownLoadingRef.current[u] = true;
    }

    try {
      const res = await client.get<{
        readonly data: {
          readonly breakdown: "week" | "day";
          readonly breakdowns?: Record<string, {
            readonly url: string;
            readonly breakdown: "week" | "day";
            readonly periods: readonly PeriodItem[];
            readonly totalCount?: number;
            readonly hasMore?: boolean;
          }>;
          readonly url?: string;
          readonly periods?: readonly PeriodItem[];
          readonly totalCount?: number;
          readonly hasMore?: boolean;
        };
      }>("/api/v2/websiteanalysis/getLandingPageTrendBreakdown", {
        params: {
          from: range.from,
          to: range.to,
          urls: missingUrls.join(","),
          breakdown,
          offset: 0,
          limit: BREAKDOWN_CHUNK_SIZE,
        },
      });

      if (currentGen !== generationRef.current) return;
      const data = res.data?.data;
      const breakdownsMap = data?.breakdowns;

      if (breakdownsMap) {
        setBreakdownsByUrl((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(breakdownsMap).map(([u, b]) => [u, b.periods ?? []])
          ),
        }));
        setBreakdownHasMore((prev) => ({
          ...prev,
          ...Object.fromEntries(
            Object.entries(breakdownsMap).map(([u, b]) => [u, b.hasMore ?? false])
          ),
        }));
        for (const [u, b] of Object.entries(breakdownsMap)) {
          breakdownHasMoreRef.current[u] = b.hasMore ?? false;
        }
      } else if (data?.url && data.periods) {
        setBreakdownsByUrl((prev) => ({ ...prev, [data.url!]: data.periods! }));
        setBreakdownHasMore((prev) => ({ ...prev, [data.url!]: data.hasMore ?? false }));
        breakdownHasMoreRef.current[data.url] = data.hasMore ?? false;
      }
    } catch (caught) {
      // Deliberately non-fatal: one row's breakdown failing must not blank the table the
      // merchant is reading. Bound rather than swallowed per .ai/02 §Errors; the defined
      // fallback is that this row simply stays collapsed.
      void caught;
    } finally {
      if (currentGen === generationRef.current) {
        setBreakdownLoading((prev) => {
          const next = { ...prev };
          for (const u of missingUrls) next[u] = false;
          return next;
        });
        for (const u of missingUrls) {
          breakdownLoadingRef.current[u] = false;
        }
      }
    }
  }, [client, range.from, range.to, breakdown]);

  // Dimension 2: Fetch breakdown for a specific landing page URL (for pagination beyond offset 0)
  const executeFetchBreakdown = useCallback(async (url: string, currentOffset: number = 0) => {
    const currentGen = generationRef.current;
    try {
      const res = await client.get<{
        readonly data: {
          readonly url: string;
          readonly breakdown: "week" | "day";
          readonly periods: readonly PeriodItem[];
          readonly totalCount?: number;
          readonly hasMore?: boolean;
          readonly offset?: number;
          readonly limit?: number;
        };
      }>("/api/v2/websiteanalysis/getLandingPageTrendBreakdown", {
        params: {
          from: range.from,
          to: range.to,
          url,
          breakdown,
          offset: currentOffset,
          limit: BREAKDOWN_CHUNK_SIZE,
        },
      });

      if (currentGen !== generationRef.current) return;
      const data = res.data?.data;
      const newPeriods = data?.periods ?? [];
      const hasMore = data?.hasMore ?? (newPeriods.length === BREAKDOWN_CHUNK_SIZE);

      setBreakdownsByUrl((prev) => {
        const existing = prev[url] ?? [];
        return {
          ...prev,
          [url]: currentOffset === 0 ? newPeriods : [...existing, ...newPeriods],
        };
      });

      breakdownHasMoreRef.current[url] = hasMore;
      setBreakdownHasMore((prev) => ({ ...prev, [url]: hasMore }));
    } catch (caught) {
      // Deliberately non-fatal: one row's breakdown failing must not blank the table the
      // merchant is reading. Bound rather than swallowed per .ai/02 §Errors; the defined
      // fallback is that this row simply stays collapsed.
      void caught;
    } finally {
      if (currentGen === generationRef.current) {
        if (currentOffset === 0) {
          breakdownLoadingRef.current[url] = false;
          setBreakdownLoading((prev) => ({ ...prev, [url]: false }));
        } else {
          breakdownLoadingMoreRef.current[url] = false;
          setBreakdownLoadingMore((prev) => ({ ...prev, [url]: false }));
        }
      }
    }
  }, [client, range.from, range.to, breakdown]);

  const fetchBreakdown = useCallback((url: string, currentOffset: number = 0) => {
    if (!url) return;
    if (currentOffset === 0) {
      void fetchBatchBreakdowns([url]);
      return;
    }
    if (breakdownLoadingMoreRef.current[url] || breakdownHasMoreRef.current[url] === false) return;
    breakdownLoadingMoreRef.current[url] = true;
    setBreakdownLoadingMore((prev) => ({ ...prev, [url]: true }));
    void executeFetchBreakdown(url, currentOffset);
  }, [fetchBatchBreakdowns, executeFetchBreakdown]);

  const handleLeftVerticalScroll = (url: string, source: HTMLDivElement) => {
    const target = rightScrollRefs.current[url];
    if (target && target.scrollTop !== source.scrollTop) {
      target.scrollTop = source.scrollTop;
    }

    // Dimension 2: When scrolling near bottom of this URL's breakdown, fetch next chunk of days/weeks
    const scrollRemaining = source.scrollHeight - source.scrollTop - source.clientHeight;
    if (scrollRemaining < 45) {
      const currentList = breakdownsByUrlRef.current[url] ?? [];
      if (
        currentList.length > 0 &&
        breakdownHasMoreRef.current[url] !== false &&
        !breakdownLoadingMoreRef.current[url] &&
        !breakdownLoadingRef.current[url]
      ) {
        void fetchBreakdown(url, currentList.length);
      }
    }
  };

  // Bind right metric breakdown container with non-passive wheel delegation:
  // - Horizontal delta / shift-wheel -> scrolls outer table horizontally
  // - Vertical delta -> scrolls whole report outer table vertically
  // - Does NOT scroll internal days vertically (vertical scroll of days only works on Column 0)
  const bindMetricWheel = useCallback((url: string) => {
    return (el: HTMLDivElement | null) => {
      const prevEl = rightScrollRefs.current[url];
      if (prevEl && (prevEl as unknown as { _wheelListener?: (e: WheelEvent) => void })._wheelListener) {
        prevEl.removeEventListener(
          "wheel",
          (prevEl as unknown as { _wheelListener: (e: WheelEvent) => void })._wheelListener
        );
      }
      rightScrollRefs.current[url] = el;
      if (el) {
        const onWheel = (e: WheelEvent) => {
          if (!outerTableRef.current) return;
          e.preventDefault();
          if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
            outerTableRef.current.scrollLeft += e.deltaX;
          } else if (e.shiftKey) {
            outerTableRef.current.scrollLeft += e.deltaY;
          } else {
            outerTableRef.current.scrollTop += e.deltaY;
          }
        };
        (el as unknown as { _wheelListener: (e: WheelEvent) => void })._wheelListener = onWheel;
        el.addEventListener("wheel", onWheel, { passive: false });
      }
    };
  }, []);

  // Dimension 1: Infinite scroll loader for Landing Page URLs
  const loadMoreUrls = useCallback(async () => {
    if (loadingMoreUrlsRef.current || loading || rows.length >= totalCount || problem !== null) return;
    loadingMoreUrlsRef.current = true;
    setLoadingMoreUrls(true);
    const nextOffset = rows.length;

    try {
      const res = await client.get<{
        readonly data: {
          readonly rows: readonly ReportRow[];
          readonly totalCount?: number;
          readonly totals?: Record<string, number>;
        };
      }>("/api/v2/websiteanalysis/getLandingPageTrend", {
        params: {
          from: range.from,
          to: range.to,
          limit: URLS_CHUNK_SIZE,
          offset: nextOffset,
          sortBy: rankingTab,
          direction: "desc",
          breakdown,
        },
      });

      const data = res.data?.data;
      const nextRows = data?.rows ?? [];
      if (nextRows.length > 0) {
        setRows((prev) => [...prev, ...nextRows]);
      }
      if (data?.totalCount !== undefined) {
        setTotalCount(data.totalCount);
      }
    } catch (caught) {
      // Deliberately non-fatal: one row's breakdown failing must not blank the table the
      // merchant is reading. Bound rather than swallowed per .ai/02 §Errors; the defined
      // fallback is that this row simply stays collapsed.
      void caught;
    } finally {
      loadingMoreUrlsRef.current = false;
      setLoadingMoreUrls(false);
    }
  }, [client, range.from, range.to, breakdown, rankingTab, rows.length, totalCount, loading, problem]);

  const handleOuterScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight <= el.clientHeight || el.scrollTop < 20) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 150 && !loadingMoreUrlsRef.current && rows.length < totalCount) {
      void loadMoreUrls();
    }
  }, [loadMoreUrls, rows.length, totalCount]);

  // Load summary URLs (initial batch of 10) on load or date range change
  const loadMainData = useCallback(async () => {
    if (problem !== null) return;
    const mine = ++ticket.current;
    generationRef.current = mine;
    setLoading(true);
    setError(null);
    setBreakdownsByUrl({});
    setBreakdownLoading({});
    setBreakdownLoadingMore({});
    setBreakdownHasMore({});

    try {
      const res = await client.get<{
        readonly data: {
          readonly rows: readonly ReportRow[];
          readonly totalCount?: number;
          readonly totals?: Record<string, number>;
          readonly timezone?: string;
          readonly dataAsOf?: string | null;
          readonly breakdown?: "week" | "day";
        };
      }>("/api/v2/websiteanalysis/getLandingPageTrend", {
        params: {
          from: range.from,
          to: range.to,
          limit: URLS_CHUNK_SIZE,
          offset: 0,
          sortBy: rankingTab,
          direction: "desc",
          breakdown,
        },
      });

      if (mine !== ticket.current) return;
      const data = res.data?.data;
      const fetchedRows = data?.rows ?? [];
      setRows(fetchedRows);
      setTotalCount(data?.totalCount ?? fetchedRows.length);
      setTotals(data?.totals ?? null);
      setTimezone(data?.timezone ?? null);
      setDataAsOf(data?.dataAsOf ?? null);
    } catch (caught) {
      if (mine !== ticket.current) return;
      setRows([]);
      setTotals(null);
      setDataAsOf(null);
      setBreakdownsByUrl({});
      setError(apiErrorMessage(caught, "Could not load Landing Page Trend."));
    } finally {
      if (mine === ticket.current) setLoading(false);
    }
  }, [client, range.from, range.to, problem, breakdown, rankingTab]);

  useEffect(() => {
    void loadMainData();
  }, [loadMainData, tenantId]);

  // Handle switching breakdown (Day <-> Week)
  const handleBreakdownChange = (next: "week" | "day") => {
    if (next === "week" && weekDisabled) return;
    setBreakdown(next);
    setBreakdownsByUrl({});
    setBreakdownLoading({});
    setBreakdownLoadingMore({});
    setBreakdownHasMore({});
  };

  // Handle switching ranking tab (Sessions | Revenue | Purchases)
  const handleRankingTabChange = (next: "session_count" | "order_value" | "customer_count") => {
    if (next === rankingTab) return;
    setRankingTab(next);
    setRows([]);
    setBreakdownsByUrl({});
    setBreakdownLoading({});
    setBreakdownLoadingMore({});
    setBreakdownHasMore({});
  };

  // Filter rows by client-side search query
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows;
    const q = searchQuery.toLowerCase();
    return rows.filter((r) => String(r["url"] ?? "").toLowerCase().includes(q));
  }, [rows, searchQuery]);

  // Dimension 1: Proactively fetch initial visible chunk as soon as rows are loaded
  useEffect(() => {
    if (filteredRows.length > 0) {
      const initialChunk = filteredRows
        .slice(0, URLS_CHUNK_SIZE)
        .map((r) => String(r["url"] ?? ""))
        .filter(Boolean);
      void fetchBatchBreakdowns(initialChunk);
    }
  }, [filteredRows, fetchBatchBreakdowns, URLS_CHUNK_SIZE]);

  // Dimension 1: On-demand batch loading of breakdown for URLs as they scroll into view
  useEffect(() => {
    const outer = outerTableRef.current;
    if (!outer || filteredRows.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleMissingUrls: string[] = [];
        for (const entry of entries) {
          if (entry.isIntersecting) {
            const targetUrl = entry.target.getAttribute("data-url");
            if (
              targetUrl &&
              !breakdownsByUrlRef.current[targetUrl] &&
              !breakdownLoadingRef.current[targetUrl]
            ) {
              visibleMissingUrls.push(targetUrl);
            }
          }
        }
        if (visibleMissingUrls.length > 0) {
          void fetchBatchBreakdowns(visibleMissingUrls);
        }
      },
      {
        root: outer,
        rootMargin: "0px 0px",
        threshold: 0.05,
      }
    );

    for (const row of filteredRows) {
      const url = String(row["url"] ?? "");
      const el = rowElementsRef.current[url];
      if (el) observer.observe(el);
    }

    return () => {
      observer.disconnect();
    };
  }, [filteredRows, fetchBatchBreakdowns]);

  const reportMeta = findReport("landing-page-trend") ?? findReport("landing-pages");
  const guide = reportMeta ? reportGuideContent(reportMeta) : null;

  const exportColumns = useMemo<readonly ReportColumn[]>(
    () => reportMeta?.columns ?? [],
    [reportMeta],
  );


  return (
    <section className="flex min-w-0 flex-col gap-4">
      {/* Header Row */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2.5 text-2xl font-bold tracking-tight text-foreground">
            <Globe className="size-6 text-primary" />
            Landing Page Analysis - Trend
            {guide && (
              <ReportGuideToggleButton
                showGuide={guideOpen}
                onToggle={() => setGuideOpen((v) => !v)}
              />
            )}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Detailed performance per landing page, broken down week by week or day by day.
          </p>
        </div>

        {/* Action Controls: Export + Date Range */}
        <div className="flex items-center gap-2">
          {reportMeta && (
            <ExportButton
              report={reportMeta}
              from={range.from}
              to={range.to}
              disabled={problem !== null}
              filterState={{
                sortBy: rankingTab,
                direction: "desc",
                status: "all",
                campaignType: "all",
                hierarchy: "",
                search: searchQuery,
                breakdown,
              }}
              visibleColumns={exportColumns}
            />
          )}

          <DateRangePicker value={range} onApply={onRangeChange} />
        </div>
      </header>

      {/* Guide Accordion */}
      {guide && (
        <ReportGuideAccordion
          showGuide={guideOpen}
          whatContent={guide.whatContent}
          {...(guide.howTitle === undefined ? {} : { howTitle: guide.howTitle })}
          {...(guide.howBadge === undefined ? {} : { howBadge: guide.howBadge })}
          howContent={guide.howContent}
        />
      )}

      {/* Error message */}
      {error && (
        <div className="text-sm font-medium text-destructive">{error}</div>
      )}

      {/* The ranking and totals may come from the 30-minute snapshot; the per-day breakdown is live. */}
      {!loading && <DataAsOfNote dataAsOf={dataAsOf} timezone={timezone} />}

      {/* Table Container Card (search and rows dropdown inside top bar) */}
      <div className="bg-card min-w-0 overflow-hidden rounded-xl border shadow-sm">
        <div className="flex flex-wrap items-center gap-3 border-b p-3">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
            <Input
              type="search"
              placeholder="Search landing page URL..."
              aria-label="Filter rows"
              className="h-9 pl-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Metric Ranking Tabs: Sessions | Revenue | Purchases */}
          <div className="flex items-center rounded-lg border bg-muted/40 p-1 text-xs">
            <button
              type="button"
              onClick={() => handleRankingTabChange("session_count")}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer",
                rankingTab === "session_count"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Sessions
            </button>
            <button
              type="button"
              onClick={() => handleRankingTabChange("order_value")}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer",
                rankingTab === "order_value"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Revenue
            </button>
            <button
              type="button"
              onClick={() => handleRankingTabChange("customer_count")}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer",
                rankingTab === "customer_count"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Purchases
            </button>
          </div>

          {/* Day / Week Switcher Tabs */}
          <div className="flex items-center rounded-lg border bg-muted/40 p-1 text-xs">
            <button
              type="button"
              onClick={() => handleBreakdownChange("day")}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-all cursor-pointer",
                breakdown === "day"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              Day
            </button>

            <button
              type="button"
              disabled={weekDisabled}
              onClick={() => handleBreakdownChange("week")}
              title={weekDisabled ? "Weekly view requires a date range greater than 7 days" : "View by week (Monday to Sunday)"}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 cursor-pointer",
                breakdown === "week"
                  ? "bg-background text-foreground shadow-xs font-semibold"
                  : "text-muted-foreground hover:text-foreground",
                weekDisabled && "opacity-45 cursor-not-allowed hover:text-muted-foreground",
              )}
            >
              Week
              {weekDisabled && <span className="text-[10px] text-muted-foreground">(&gt;7d)</span>}
            </button>
          </div>

          <div className="flex-1" />

          <span className="text-xs text-muted-foreground font-medium px-2 py-1 rounded bg-muted/40 border">
            Showing {filteredRows.length}{totalCount > 0 ? ` of ${totalCount.toLocaleString("en-US")}` : ""} URLs
          </span>
        </div>

        <div
          ref={outerTableRef}
          onScroll={handleOuterScroll}
          className="max-h-[min(65svh,44rem)] overflow-auto transition-opacity"
        >
          <table
            className="table-fixed border-separate border-spacing-0 text-sm"
            style={{ width: `${totalTableWidth}px`, minWidth: "100%" }}
          >
            <colgroup>
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w}px` }} />
              ))}
            </colgroup>

            {/* Table Header: Only header <th> has right border for resize handle */}
            <thead className="sticky top-0 z-30">
              <tr>
                {headerColumns.map((col: ReportColumn, idx: number) => (
                  <th
                    key={col.key}
                    className={cn(
                      "row-head text-foreground h-11 border-b border-r border-border px-3 text-[13px] font-semibold select-none whitespace-nowrap relative bg-muted/80 backdrop-blur-md",
                      idx === 0 ? "sticky left-0 z-10 text-left" : "text-right pl-3 pr-4",
                    )}
                  >
                    <span>{col.label}</span>
                    <TableResizeHandle onResize={(e) => startResize(idx, e)} />
                  </th>
                ))}
              </tr>
            </thead>

            {/* Skeleton Shimmer UI on Initial Load / Loading */}
            {loading && rows.length === 0 ? (
              <tbody aria-busy="true" aria-label="Loading report">
                {Array.from({ length: 8 }, (_, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className={rowIndex % 2 === 1 ? "bg-muted/15" : "bg-card"}
                  >
                    {headerColumns.map((column, index) => (
                      <td
                        key={column.key}
                        className={cn(
                          "overflow-hidden border-b px-3 py-3",
                          index === 0 && "sticky left-0 z-20 bg-card border-r border-border",
                        )}
                      >
                        <Skeleton
                          className={cn("h-4", index === 0 ? "w-48" : "ml-auto w-16")}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              /* Table Body with Real Data */
              <tbody>
                {filteredRows.length === 0 && !loading && (
                  <tr>
                    <td
                      colSpan={headerColumns.length}
                      className="p-8 text-center text-muted-foreground italic text-sm"
                    >
                      No landing pages found in this date range.
                    </td>
                  </tr>
                )}

                {filteredRows.map((parentRow) => {
                  const url = String(parentRow["url"] ?? "");
                  const periods = breakdownsByUrl[url] ?? [];
                  const isInitialLoading = Boolean(breakdownLoading[url] && periods.length === 0);
                  const isLoadingMore = Boolean(breakdownLoadingMore[url]);

                  return (
                    <React.Fragment key={url}>
                      {/* Parent Row: Landing Page URL + Grand Total */}
                      <tr
                        ref={(el) => { rowElementsRef.current[url] = el; }}
                        data-url={url}
                        className="bg-muted/30 font-semibold border-b hover:bg-muted/50 transition-colors"
                      >
                        <td className="sticky left-0 z-20 bg-card border-r border-border border-b px-3 py-2.5 max-w-0">
                          <div className="flex items-center justify-between gap-2 max-w-full min-w-0">
                            <span
                              className="font-semibold text-foreground text-[13px] truncate"
                              title={url}
                            >
                              {url}
                            </span>
                            <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                              Grand Total
                            </span>
                          </div>
                        </td>
                        {metricColumns.map((col: ReportColumn) => (
                          <td
                            key={col.key}
                            className="text-right tabular-nums border-b pl-3 pr-4 py-2.5 whitespace-nowrap text-[13px]"
                          >
                            {cellText(col.kind, parentRow[col.key], col.key)}
                          </td>
                        ))}
                      </tr>

                      {/* Breakdown Rows: Either Loading Shimmer OR Real Data */}
                      {isInitialLoading ? (
                        <tr>
                          {/* Skeleton Column 0 */}
                          <td className="sticky left-0 z-20 bg-card border-r border-border border-b p-0 align-top max-w-0">
                            <div className="h-[144px] overflow-hidden px-4 py-3 flex flex-col justify-around">
                              <Skeleton className="h-4 w-44" />
                              <Skeleton className="h-4 w-52" />
                              <Skeleton className="h-4 w-40" />
                              <Skeleton className="h-4 w-48" />
                            </div>
                          </td>
                          {/* Skeleton Metric Columns */}
                          <td colSpan={metricColumns.length} className="p-0 border-b align-top">
                            <div className="h-[144px] overflow-hidden px-4 py-3 flex flex-col justify-around">
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-full" />
                              <Skeleton className="h-4 w-full" />
                            </div>
                          </td>
                        </tr>
                      ) : periods.length > 0 ? (
                        <tr>
                          {/* Column 0: Fixed natively on the left with sticky left-0 */}
                          <td className="sticky left-0 z-20 bg-card border-r border-border border-b p-0 align-top max-w-0">
                            <div
                              ref={(el) => { leftScrollRefs.current[url] = el; }}
                              onScroll={(e) => handleLeftVerticalScroll(url, e.currentTarget)}
                              className="max-h-[144px] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:w-0 [&::-webkit-scrollbar]:h-0 overscroll-contain"
                            >
                              <table className="w-full table-fixed border-collapse">
                                <tbody>
                                  {periods.map((period) => (
                                    <tr
                                      key={`${url}-${period.period_num}`}
                                      className="h-9 border-b hover:bg-muted/20 transition-colors"
                                    >
                                      <td className="pl-8 pr-3 py-1.5 text-[13px] max-w-0">
                                        <div className="flex items-center gap-1.5 min-w-0 max-w-full">
                                          <span className="font-medium text-foreground shrink-0">
                                            {period.period_label}
                                          </span>
                                          <span className="text-muted-foreground text-xs truncate">
                                            ({period.period_start} to {period.period_end})
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                  {isLoadingMore && (
                                    <tr className="h-9 border-b">
                                      <td className="pl-8 pr-3 py-1.5 text-[12px] text-muted-foreground italic">
                                        Loading more...
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>

                          {/* Columns 1-17: Scrolls horizontally in lockstep with outer table header */}
                          <td colSpan={metricColumns.length} className="p-0 border-b align-top">
                            <div
                              ref={bindMetricWheel(url)}
                              className="max-h-[144px] overflow-hidden"
                            >
                              <table
                                className="table-fixed border-separate border-spacing-0 text-sm"
                                style={{ width: `${metricColumnsTotalWidth}px`, minWidth: "100%" }}
                              >
                                <colgroup>
                                  {metricColumnsWidths.map((w, i) => (
                                    <col key={i} style={{ width: `${w}px` }} />
                                  ))}
                                </colgroup>
                                <tbody>
                                  {periods.map((period) => (
                                    <tr
                                      key={`${url}-${period.period_num}`}
                                      className="h-9 border-b hover:bg-muted/20 transition-colors"
                                    >
                                      {metricColumns.map((col: ReportColumn) => (
                                        <td
                                          key={col.key}
                                          className="text-right tabular-nums pl-3 pr-4 py-1.5 text-[13px] whitespace-nowrap text-muted-foreground"
                                        >
                                          {cellText(col.kind, period[col.key], col.key)}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                  {isLoadingMore && (
                                    <tr className="h-9 border-b">
                                      {metricColumns.map((col: ReportColumn) => (
                                        <td key={col.key} className="pl-3 pr-4 py-1.5 text-right">
                                          <Skeleton className="h-3 w-12 ml-auto" />
                                        </td>
                                      ))}
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
                {loadingMoreUrls && (
                  <tr>
                    <td
                      colSpan={headerColumns.length}
                      className="py-3 px-4 text-center text-xs text-muted-foreground bg-muted/20 border-b"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <div className="size-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span>Loading more landing pages...</span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            )}

            {/* Overall Totals Footer: Fixed/Sticky at bottom while middle rows scroll */}
            {!loading && rows.length > 0 && (
              <tfoot className="sticky bottom-0 z-30 font-semibold">
                <tr className="bg-muted/95 backdrop-blur-md">
                  <td className="sticky left-0 bottom-0 z-40 bg-muted/95 backdrop-blur-md border-r border-t-2 border-border px-3 py-2.5 text-[13px] max-w-0 shadow-[0_-2px_6px_rgba(0,0,0,0.06)]">
                    <span className="font-semibold text-foreground">Overall Totals</span>
                  </td>
                  {metricColumns.map((col: ReportColumn) => {
                    const totalVal = totals ? totals[col.key] : undefined;
                    return (
                      <td
                        key={col.key}
                        className="sticky bottom-0 z-30 bg-muted/95 backdrop-blur-md text-right tabular-nums border-t-2 border-border pl-3 pr-4 py-2.5 whitespace-nowrap text-[13px] font-semibold text-foreground shadow-[0_-2px_6px_rgba(0,0,0,0.06)]"
                      >
                        {totalVal !== undefined
                          ? cellText(col.kind, totalVal, col.key)
                          : "—"}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Infinite Scroll Information Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
        <span>
          Showing {filteredRows.length} of {totalCount.toLocaleString("en-US")} landing pages
        </span>
        {rows.length < totalCount && (
          <span className="italic">
            Scroll down the table to load more landing pages
          </span>
        )}
      </div>
    </section>
  );
}
